import type { IGuardRepository, PaginatedResponse } from '@yawi/domain';
import type {
  CreateGuardAccessLogDTO,
  CreateRestrictionRequest,
  MyRestrictions,
  CurrentShiftResponse,
  DelinquentUnit,
  DirectoryParams,
  DirectoryResident,
  EmergencyContact,
  GuardAccessLog,
  GuardAccessLogParams,
  GuardDashboard,
  PanicAlertRequest,
  PanicAlertResponse,
  Restriction,
  Shift,
  ShiftParams,
  ShiftWithDetails,
  Supplier,
  GuardRoute,
  GuardRouteDetail,
  GuardRoutePoint,
  RouteHistoryParams,
  ShiftStartContext,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiGuardRepository implements IGuardRepository {
  private readonly basePath = 'v1/guard';

  // ========== DIRECTORY ==========

  async getDirectory(params?: DirectoryParams): Promise<PaginatedResponse<DirectoryResident>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.search) queryParams.set('search', params.search);
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));

    const queryString = queryParams.toString();
    const url = `${this.basePath}/directory${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<DirectoryResident>>(url);
  }

  // ========== DELINQUENT UNITS ==========

  async getDelinquentUnits(): Promise<DelinquentUnit[]> {
    const client = getApiClient();
    return await client.get<DelinquentUnit[]>(`${this.basePath}/delinquent-units`);
  }

  // ========== DASHBOARD ==========

  async getDashboard(): Promise<GuardDashboard> {
    const client = getApiClient();
    return await client.get<GuardDashboard>(`${this.basePath}/dashboard`);
  }

  // ========== SHIFTS ==========

  async getActiveShift(): Promise<Shift | null> {
    const client = getApiClient();
    const response = await client.get<CurrentShiftResponse>(`${this.basePath}/shift/current`);
    return response.hasActiveShift ? response.shift! : null;
  }

  async getShifts(params?: ShiftParams): Promise<PaginatedResponse<Shift>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.status) queryParams.set('status', params.status);
    if (params?.guardId) queryParams.set('guardId', params.guardId);
    if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.set('dateTo', params.dateTo);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/shift${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<Shift>>(url);
  }

  async getShiftById(id: string): Promise<ShiftWithDetails> {
    const client = getApiClient();
    return await client.get<ShiftWithDetails>(`${this.basePath}/shift/${id}`);
  }

  async startShift(
    notes?: string,
    latitude?: number,
    longitude?: number,
    context?: ShiftStartContext
  ): Promise<Shift> {
    const client = getApiClient();
    return await client.post<Shift>(`${this.basePath}/shift/start`, {
      ...context,
      notes,
      // StartShiftDto/EndShiftDto aceptan latitude/longitude (forbidNonWhitelisted rechaza start*/end*).
      latitude,
      longitude,
    });
  }

  async endShift(notes?: string, latitude?: number, longitude?: number): Promise<Shift> {
    const client = getApiClient();
    return await client.post<Shift>(`${this.basePath}/shift/end`, {
      notes,
      latitude,
      longitude,
    });
  }

  // ========== ACCESS LOGS ==========

  async getAccessLogs(params?: GuardAccessLogParams): Promise<PaginatedResponse<GuardAccessLog>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.type) queryParams.set('type', params.type);
    if (params?.method) queryParams.set('method', params.method);
    if (params?.personType) queryParams.set('personType', params.personType);
    if (params?.unitId) queryParams.set('unitId', params.unitId);
    if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.set('dateTo', params.dateTo);
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/logs${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<GuardAccessLog>>(url);
  }

  async createAccessLog(data: CreateGuardAccessLogDTO): Promise<GuardAccessLog> {
    const client = getApiClient();
    return await client.post<GuardAccessLog>(`${this.basePath}/register-access`, data);
  }

  // ========== RESTRICTIONS ==========

  async getRestrictions(): Promise<Restriction[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<Restriction>>(`${this.basePath}/restrictions`);
    return response.data || [];
  }

  async createRestriction(request: CreateRestrictionRequest): Promise<Restriction> {
    const client = getApiClient();
    return await client.post<Restriction>(`${this.basePath}/restrictions`, request);
  }

  async deleteRestriction(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.basePath}/restrictions/${id}`);
  }

  async getMyRestrictions(): Promise<MyRestrictions> {
    const client = getApiClient();
    return await client.get<MyRestrictions>('v1/restrictions/me');
  }

  // ========== EMERGENCY ==========

  async getEmergencyContacts(): Promise<EmergencyContact[]> {
    const client = getApiClient();
    return await client.get<EmergencyContact[]>(`${this.basePath}/emergency/contacts`);
  }

  async triggerPanicAlert(request?: PanicAlertRequest): Promise<PanicAlertResponse> {
    const client = getApiClient();
    return await client.post<PanicAlertResponse>(`${this.basePath}/emergency/panic`, request || {});
  }

  // ========== SUPPLIERS ==========

  async getSuppliers(): Promise<Supplier[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<Supplier>>(`v1/suppliers`);
    return response.data || [];
  }

  // ========== GPS ROUTES ==========

  async startRoute(clientEventId: string, startedAt: string): Promise<GuardRoute> {
    const client = getApiClient();
    return await client.post<GuardRoute>(`${this.basePath}/routes/start`, { clientEventId, startedAt });
  }

  async getCurrentRoute(): Promise<GuardRoute | null> {
    const client = getApiClient();
    // Nest serializa un `null` como cuerpo vacío.
    return (await client.get<GuardRoute | null | ''>(`${this.basePath}/routes/current`)) || null;
  }

  async addRoutePoints(
    routeId: string,
    points: GuardRoutePoint[]
  ): Promise<{ received: number; stored: number }> {
    const client = getApiClient();
    return await client.post(`${this.basePath}/routes/${routeId}/points`, { points });
  }

  async finishRoute(routeId: string, endedAt: string): Promise<GuardRoute> {
    const client = getApiClient();
    return await client.post<GuardRoute>(`${this.basePath}/routes/${routeId}/finish`, { endedAt });
  }

  async getRoute(routeId: string): Promise<GuardRouteDetail> {
    const client = getApiClient();
    return await client.get<GuardRouteDetail>(`${this.basePath}/routes/${routeId}`);
  }

  async getRoutes(params?: RouteHistoryParams): Promise<PaginatedResponse<GuardRoute>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.guardId) queryParams.set('guardId', params.guardId);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.set('dateTo', params.dateTo);

    const queryString = queryParams.toString();
    return await client.get<PaginatedResponse<GuardRoute>>(
      `${this.basePath}/routes${queryString ? `?${queryString}` : ''}`
    );
  }
}
